import { PHONE_DISPLAY, PHONE_NUMBER } from '@/constants/contact';
import { OFFICIAL_INSTAGRAM_PROFILE_URL } from '@/constants/social';
import { brand } from '@/config/brand';

export const SITE_URL = 'https://workshopsbyaabha.com';
export const SITE_NAME = 'Workshops by Aabha';

/**
 * Share images with their real pixel dimensions, so `og:image:width`/`height`
 * stay accurate. Landscape assets are preferred because social cards crop to
 * roughly 1.91:1.
 */
export const OG_IMAGES = {
  kintsugi: {
    url: `${SITE_URL}/assets/images/kintsugi%20hero%20image.png`,
    width: '2752',
    height: '1356',
    alt: 'Ceramic piece repaired with gold at a Kintsugi workshop in Bangalore',
  },
  corporate: {
    url: `${SITE_URL}/assets/images/Corporate%20workshops%20hero%20team.png`,
    width: '1024',
    height: '682',
    alt: 'Corporate team taking part in a creative team-building workshop in Bangalore',
  },
  events: {
    url: `${SITE_URL}/assets/images/events-hero-main.png`,
    width: '1024',
    height: '444',
    alt: 'Guests creating artwork together at a private celebration workshop in Bangalore',
  },
  calendar: {
    url: `${SITE_URL}/assets/images/events-weekend-new.png`,
    width: '1024',
    height: '768',
    alt: 'Weekend creative workshop session in Bangalore',
  },
  contact: {
    url: `${SITE_URL}/assets/images/contact-hero-editorial.png`,
    width: '1024',
    height: '768',
    alt: 'Workshop materials laid out ahead of a creative session in Bangalore',
  },
};

export const DEFAULT_OG_IMAGE = OG_IMAGES.kintsugi.url;
export const DEFAULT_OG_IMAGE_ALT = OG_IMAGES.kintsugi.alt;
export const DEFAULT_OG_IMAGE_WIDTH = OG_IMAGES.kintsugi.width;
export const DEFAULT_OG_IMAGE_HEIGHT = OG_IMAGES.kintsugi.height;

/** Maps an OG image entry onto the props `PageSeo` expects. */
function ogImage(key) {
  const { url, alt, width, height } = OG_IMAGES[key];
  return { image: url, imageAlt: alt, imageWidth: width, imageHeight: height };
}

/** Stable entity IDs so every page references the same business across the site. */
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const BUSINESS_ID = `${SITE_URL}/#business`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

export const LOCAL_BUSINESS = {
  name: SITE_NAME,
  telephone: `+91${PHONE_NUMBER}`,
  telephoneDisplay: PHONE_DISPLAY,
  email: brand.email,
  instagram: OFFICIAL_INSTAGRAM_PROFILE_URL,
  addressLocality: 'Bengaluru',
  addressRegion: 'Karnataka',
  addressCountry: 'IN',
  areaServed: ['Bengaluru', 'Bangalore', 'Karnataka', 'India'],
};

/**
 * Describes how sessions actually run: curated partner venues in Bengaluru plus
 * on-site delivery for corporate and private groups. No fixed storefront is claimed.
 */
export const BUSINESS_DESCRIPTION =
  'Workshops by Aabha is a Bengaluru-based creative workshop studio offering Japanese Kintsugi experiences, weekend art workshops, corporate team-building sessions and private creative gatherings. Sessions run at curated venues across Bengaluru and on-site at your location for private and corporate groups.';

/** @param {string} path */
export function absoluteUrl(path = '/') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === '/') return `${SITE_URL}/`;
  return `${SITE_URL}${normalized}`;
}

export const PAGE_SEO = {
  home: {
    path: '/',
    title: 'Workshops by Aabha | Creative & Kintsugi Workshops in Bangalore',
    description:
      'Workshops by Aabha runs hands-on Kintsugi, art and weekend creative workshops in Bangalore, plus corporate team building and private celebrations. Book on WhatsApp.',
    image: DEFAULT_OG_IMAGE,
  },
  corporate: {
    path: '/corporate',
    title: 'Corporate Workshops & Team Building in Bangalore | Aabha',
    description:
      'Corporate workshops in Bangalore for team building and employee engagement — Kintsugi, heritage art and mindful making, hosted at your office or a curated venue.',
    ...ogImage('corporate'),
  },
  workshops: {
    path: '/workshops',
    title: 'Creative & Art Workshops in Bangalore | Workshops by Aabha',
    description:
      'Explore hands-on creative and art workshops in Bangalore for adults and groups — Kintsugi, traditional Indian crafts, terrariums and more. All materials provided.',
    image: DEFAULT_OG_IMAGE,
  },
  kintsugi: {
    path: '/workshops/japanese-kintsugi',
    title: 'Kintsugi Workshop in Bangalore | Workshops by Aabha',
    description:
      'Attend a beginner-friendly Japanese Kintsugi workshop in Bangalore. Learn the art of golden repair in 1.5–3 hours, materials included, and take your piece home.',
    ...ogImage('kintsugi'),
  },
  events: {
    path: '/events',
    title: 'Private Creative Workshops & Events in Bangalore | Aabha',
    description:
      'Book private creative workshops in Bangalore for celebrations, birthdays, kitty parties and group gatherings. Curated art experiences hosted at your chosen venue.',
    ...ogImage('events'),
  },
  calendar: {
    path: '/calendar',
    title: 'Upcoming Workshops in Bangalore | Workshops by Aabha',
    description:
      'See upcoming weekend workshops in Bangalore — Kintsugi café sessions, terrarium making and traditional art. Message us on WhatsApp for live dates and seats.',
    ...ogImage('calendar'),
  },
  gallery: {
    path: '/gallery',
    title: 'Workshop Gallery | Creative Experiences by Aabha',
    description:
      'Photos from real Kintsugi, corporate team building and weekend art workshops run by Workshops by Aabha across Bangalore.',
    image: DEFAULT_OG_IMAGE,
  },
  about: {
    path: '/about',
    title: 'About Workshops by Aabha | Creative Experiences in Bangalore',
    description:
      'Workshops by Aabha curates creative workshops and art experiences in Bengaluru and across India for teams, celebrations and weekend learners. Meet the studio.',
    ...ogImage('corporate'),
  },
  contact: {
    path: '/contact',
    title: 'Contact Workshops by Aabha | Bangalore Workshops',
    description:
      'Contact Workshops by Aabha in Bangalore for corporate events, private celebrations, weekend workshops and school programmes. Call or WhatsApp to book a session.',
    ...ogImage('contact'),
  },
  privacy: {
    path: '/privacy-policy',
    title: 'Privacy Policy | Workshops by Aabha',
    description:
      'Privacy policy for Workshops by Aabha in Bangalore. How we collect, store, and protect your information when you enquire about or book creative workshops and events.',
    noIndex: false,
  },
  terms: {
    path: '/terms',
    title: 'Terms & Guidelines | Workshops by Aabha',
    description:
      'Terms and guidelines for Workshops by Aabha bookings — creative workshops, corporate events, and weekend sessions in Bangalore. Please read before you reserve.',
    noIndex: false,
  },
  cancellation: {
    path: '/cancellation-refund',
    title: 'Cancellation & Refund Policy | Workshops by Aabha',
    description:
      'Cancellation and refund policy for Workshops by Aabha in Bangalore — corporate workshops, private celebrations, and weekend sessions. Rescheduling details included.',
    noIndex: false,
  },
};

export const SITEMAP_PATHS = [
  '/',
  '/corporate',
  '/workshops',
  '/workshops/japanese-kintsugi',
  '/events',
  '/calendar',
  '/gallery',
  '/about',
  '/contact',
  '/privacy-policy',
  '/terms',
  '/cancellation-refund',
];

function buildPostalAddress() {
  return {
    '@type': 'PostalAddress',
    addressLocality: LOCAL_BUSINESS.addressLocality,
    addressRegion: LOCAL_BUSINESS.addressRegion,
    addressCountry: LOCAL_BUSINESS.addressCountry,
  };
}

function buildAreaServed() {
  return LOCAL_BUSINESS.areaServed.map((name) => ({ '@type': 'City', name }));
}

/**
 * Site-wide entity graph. Organization, LocalBusiness and WebSite share stable
 * @id values so every page resolves to the same business entity.
 */
export function buildSiteGraph() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/assets/images/logo-transparent.png`,
        },
        image: DEFAULT_OG_IMAGE,
        telephone: LOCAL_BUSINESS.telephone,
        email: LOCAL_BUSINESS.email,
        description: BUSINESS_DESCRIPTION,
        sameAs: [LOCAL_BUSINESS.instagram],
        areaServed: buildAreaServed(),
      },
      {
        '@type': 'LocalBusiness',
        '@id': BUSINESS_ID,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        parentOrganization: { '@id': ORGANIZATION_ID },
        image: DEFAULT_OG_IMAGE,
        telephone: LOCAL_BUSINESS.telephone,
        email: LOCAL_BUSINESS.email,
        priceRange: '₹₹',
        address: buildPostalAddress(),
        areaServed: buildAreaServed(),
        sameAs: [LOCAL_BUSINESS.instagram],
        description: BUSINESS_DESCRIPTION,
        knowsAbout: [
          'Kintsugi',
          'Japanese Kintsugi',
          'Wabi-sabi',
          'Creative workshops',
          'Art workshops',
          'Corporate team building',
        ],
        makesOffer: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Kintsugi workshop in Bangalore',
              url: absoluteUrl('/workshops/japanese-kintsugi'),
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Corporate workshops and team building in Bangalore',
              url: absoluteUrl('/corporate'),
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Private creative workshops and events in Bangalore',
              url: absoluteUrl('/events'),
            },
          },
        ],
      },
      {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: `${SITE_URL}/`,
        name: SITE_NAME,
        publisher: { '@id': ORGANIZATION_ID },
        inLanguage: 'en-IN',
      },
    ],
  };
}

/**
 * @param {{ name: string, path: string }[]} trail
 */
export function buildBreadcrumbSchema(trail) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

/**
 * @param {{ question: string, answer: string }[]} faqs
 */
export function buildFaqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Workshop formats are ongoing services rather than scheduled events, so they are
 * described as Service. Use `buildEventSchema` only once a real date is confirmed.
 *
 * @param {{ name: string, description?: string, image?: string, path?: string, serviceType?: string }} service
 */
export function buildServiceSchema(service) {
  const url = absoluteUrl(service.path || '/workshops');
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    serviceType: service.serviceType || 'Creative workshop',
    description: service.description,
    ...(service.image ? { image: service.image } : {}),
    url,
    provider: { '@id': BUSINESS_ID },
    areaServed: buildAreaServed(),
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceUrl: url,
      servicePhone: LOCAL_BUSINESS.telephone,
    },
  };
}

/**
 * Only emit Event markup for sessions with a confirmed date. Anything without a
 * real `startDate` must use `buildServiceSchema` instead — fabricated dates,
 * venues or prices would be misleading structured data.
 *
 * @param {{
 *   title: string;
 *   startDate: string;
 *   endDate?: string;
 *   description?: string;
 *   image?: string;
 *   path?: string;
 *   venueName?: string;
 *   price?: string;
 * }} event
 */
export function buildEventSchema(event) {
  if (!event?.startDate) {
    throw new Error('buildEventSchema requires a confirmed startDate.');
  }

  const url = absoluteUrl(event.path || '/calendar');

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    description: event.description,
    ...(event.image ? { image: event.image } : {}),
    startDate: event.startDate,
    ...(event.endDate ? { endDate: event.endDate } : {}),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    url,
    location: {
      '@type': 'Place',
      name: event.venueName || 'Bengaluru',
      address: buildPostalAddress(),
    },
    organizer: { '@id': ORGANIZATION_ID },
    ...(event.price
      ? {
          offers: {
            '@type': 'Offer',
            url,
            price: event.price,
            priceCurrency: 'INR',
            availability: 'https://schema.org/InStock',
          },
        }
      : {}),
  };
}
